import React, { useEffect, useRef, useState } from "react";
import { theme } from "../../theme/theme";
import { formatTime } from "../../utils/formatMediaTime";
import { V3IconDownload, V3IconMuted, V3IconVolFull } from "../Icons";
import { V3RangeInput } from "../RangeInput";
import { V3Tooltip } from "../Tooltip";
import { V3HeadlineH5 } from "../Typography";
import { VolumeRockerWrapper, VolumeRange } from "../VideoPlayer/V3VideoPlayer.styles";
import { V3APause, V3APlay } from "./V3AButtons";
import { V3ACustomControls, V3Audio, V3AWrapper } from "./V3AudioPlayer.styles";

const getFormat = (type?: string) => {
    // HOTFIX - browser can process some formats so set formats they understand
    if (!type || type === "audio/x-mpeg" || type === "audio/m4a") return 'audio/mpeg';
    if (type === "application/octet-stream") return "audio/ogg";
    // Otherwise we return original format
    return type;
}

type V3AudioPlayerProps = {
    type?: string,
    src: string,
}

export const V3AudioPlayer = ({ type, src }: V3AudioPlayerProps) => {

    const [hasError, setHasError] = useState(false);
    const [supportsAudio, setSupportsAudio] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [totalDuration, setTotalDuration] = useState("00:00");
    const [progressValue, setProgressValue] = useState("0");

    const [isPaused, setIsPaused] = useState(true);

    const audioEl = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        const supportsAudio = !!document.createElement('audio').canPlayType(getFormat(type));

        setSupportsAudio(supportsAudio);

        const currAudioEl = audioEl.current;

        // Hide the default controls
        if (currAudioEl) currAudioEl.controls = false;

    }, [audioEl.current]);

    const TogglePlay = () => {
        const currAudioEl = audioEl.current;
        if (!currAudioEl) return;
        if (currAudioEl.paused || currAudioEl.ended) currAudioEl.play();
        else currAudioEl.pause();
        setIsPaused(!isPaused);
    }

    const ToggleMuted = () => {
        const currAudioEl = audioEl.current;
        if (!currAudioEl) return;
        currAudioEl.muted = !currAudioEl.muted;
        setIsMuted(!isMuted);
    }

    const updateTotalDuration = (time: number) => {
        //update video current time
        const currentTime = Math.round(time);
        const formattedTime = formatTime(currentTime);
        setTotalDuration(`${formattedTime.minutes}:${formattedTime.seconds}`);
    }

    const showMutedVolume = isMuted || (audioEl && audioEl.current?.volume === 0);

    return <V3AWrapper>
        <V3Audio $show={!supportsAudio}>
            <audio
                ref={audioEl}
                controls
                preload="metadata"
                playsInline
                onError={() => {
                    setHasError(true);
                }}
                onEnded={() => {
                    setIsPaused(true);
                }}
                onLoadedMetadata={() => {
                    const currAudioEl = audioEl.current;
                    if (!currAudioEl) return;

                    updateTotalDuration(currAudioEl.duration)
                }}
                onTimeUpdate={() => {
                    const currAudioEl = audioEl.current;
                    if (!currAudioEl) return;

                    setProgressValue((currAudioEl.currentTime * 100 / currAudioEl.duration).toString());

                    updateTotalDuration(currAudioEl.duration)
                }}
            >
                <source src={src} type={getFormat(type)} />
            </audio>
        </V3Audio>
        <V3ACustomControls $show={supportsAudio}>
            <div>
                {
                    isPaused ? <V3APlay onClick={TogglePlay} /> : <V3APause onClick={TogglePlay} />
                }
            </div>
            <div>
                <V3RangeInput
                    min="0"
                    max="100"
                    step="0.01"
                    value={progressValue}
                    barColor={theme.v3.colors.grey88}
                    onChange={(e) => {
                        if (hasError) return;

                        const currAudioEl = audioEl.current;
                        if (!currAudioEl) return;

                        setProgressValue(e.target.value);

                        const val = e.target.value;
                        const targetVal = parseFloat(val);

                        if (isFinite(targetVal)) {
                            currAudioEl.currentTime = targetVal / 100 * currAudioEl.duration;
                        }
                    }}
                />
            </div>
            <div>
                <V3HeadlineH5><strong>{totalDuration}</strong></V3HeadlineH5>
            </div>
            <div>
                <V3Tooltip
                    referenceContent={<V3Tooltip
                        background="white"
                        referenceContent={<>
                            {
                                showMutedVolume ? <V3IconMuted onClick={ToggleMuted} /> : <V3IconVolFull onClick={ToggleMuted} />
                            }
                        </>}
                        tooltipContent={<VolumeRockerWrapper>
                            <VolumeRange>
                                <V3RangeInput
                                    min="0"
                                    max="1"
                                    defaultValue="0.5"
                                    step="0.05"
                                    barColor={theme.v3.colors.grey88}
                                    thumbSize={10}
                                    onChange={(e) => {
                                        const currAudioEl = audioEl.current;
                                        if (!currAudioEl) return;

                                        const targetVal = parseFloat(e.target.value);

                                        if (isFinite(targetVal)) {
                                            currAudioEl.volume = targetVal;
                                        }
                                    }}
                                />
                            </VolumeRange>
                        </VolumeRockerWrapper>}
                    />}
                    tooltipContent={showMutedVolume ? "Unmute" : "Mute"}
                    placement="bottom"
                />
            </div>
            <div>
                <V3IconDownload onClick={() => { window.open(src) }} />
            </div>
        </V3ACustomControls>
    </V3AWrapper>
}